import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TenantPermissions } from '@Core/enums/codegen/tenant-permissions.enum';
import { AccountsService } from '@Services/account-accounts.service';
import { SearchResult, SearchDataType } from '@Shared/Components/sm-quick-search-card/sm-quick-search-card.component';
import _ from 'lodash';
import { Subscription } from 'rxjs';

@Component({
    standalone: false,
    selector: 'account-search',
    templateUrl: './account-search.component.html',
    styleUrls: ['./account-search.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountSearchComponent implements OnInit, OnDestroy {

    private static readonly HighlightStyleElementId = 'account-search-highlight-style';

    public searchFocus: boolean = false;
    public searching: boolean = false;
    public searchHasValue: boolean = false;
    public searchValue: string = "";
    public searchMessage: string = "";
    public overSearch: boolean = false;
    public activeSearchTerm: string = '';
    public foundAccounts: SearchResult[] = new Array<SearchResult>();
    public searchMenuItems: Array<{ id: string; label: string; supportingText?: string }> = [];

    public searchDebounceTimeout: any;
    public searchSubscription$: Subscription;

    private highlightFrameHandle: number | null = null;
    private appliedHighlightKey: string | null = null;

    public searchType: SearchDataType = SearchDataType.Account;

    public TENANT_PERMISSIONS = TenantPermissions;
    @ViewChild('searchMenu') private searchMenuElement?: ElementRef<HTMLElement>;

    constructor(
        private accountService: AccountsService,
        private router: Router,
        private changeDetection: ChangeDetectorRef
    ) { }

    public ngOnInit(): void {
        this.setDefaultSearchMessage();
    }

    public ngOnDestroy(): void {
        this.clearSearchTimeout();
        this.cancelMenuHighlightUpdate();
        this.searchSubscription$?.unsubscribe();
    }

    public onSearchFocus(): void {
        this.searchFocus = true;
        this.scheduleMenuHighlightUpdate();
    }

    public onSearchBlur(): void {
        setTimeout(() => {
            if (!this.overSearch) {
                this.searchFocus = false;
                this.changeDetection.detectChanges();
            }
        }, 0);
    }

    public onSearchOver(): void {
        this.overSearch = true;
    }

    public onSearchOut(): void {
        this.overSearch = false;
    }

    public onResultsMouseDown(): void {
        this.overSearch = true;
    }

    public onSearchKeyup(event: KeyboardEvent): void {
        this.clearSearchTimeout();

        const searchValue = (<HTMLInputElement>event.target).value;
        this.searchValue = searchValue;

        if (searchValue.length > 0) {
            this.searchHasValue = true;
            this.searchMessage = "Minimum 3 characters to search";
        }
        else {
            this.searchHasValue = false;
            this.foundAccounts = [];
            this.setDefaultSearchMessage();
        }
        if (searchValue.length > 2) {
            this.performSearch(searchValue);
        } else {
            this.searching = false;
        }
    }

    public onSearchInputChange(event: Event): void {
        this.clearSearchTimeout();

        const searchValue = ((event as CustomEvent)?.detail?.value ?? '').toString();
        this.searchValue = searchValue;
        this.activeSearchTerm = searchValue;

        if (searchValue.length > 0) {
            this.searchHasValue = true;
            this.searchMessage = "Minimum 3 characters to search";
        }
        else {
            this.searchHasValue = false;
            this.foundAccounts = [];
            this.searchMenuItems = [];
            this.setDefaultSearchMessage();
        }
        if (searchValue.length > 2) {
            this.performSearch(searchValue);
        } else {
            this.searching = false;
        }
    }

    private clearSearchTimeout() {
        clearTimeout(this.searchDebounceTimeout);
        this.searchDebounceTimeout = null;
    }

    private setDefaultSearchMessage() {
        this.searchMessage = "Start typing to search";
    }

    private performSearch(searchValue: string): void {
        var self = this;
        this.searching = true;
        this.searchMessage = "Searching...";

        if (this.searchHasValue) {
            this.searchDebounceTimeout = setTimeout(() => {
                // If there is currently a search in progress, ignore the result.
                if (this.searchSubscription$)
                    this.searchSubscription$.unsubscribe();

                this.searchSubscription$ = this.accountService.searchAccounts(searchValue).subscribe(
                    (response: any) => {
                        // Do this here and not at the beginning of the function to prevent some weird UI where the search list disappears temporarily
                        this.foundAccounts = [];
                        var responseContent = response.Content; // This should be an array of objects.
                        _.forEach(responseContent, obj => {

                            // We are going to deserialize this to our Search Account object.
                            var searchAccount = new SearchResult();
                            searchAccount.score = obj["@search.score"];
                            searchAccount.highlights = obj["@search.highlights"];
                            searchAccount.data = obj.document;  // Not an actual Account object.
                            self.foundAccounts.push(searchAccount);
                        });
                        this.searchMenuItems = this.buildMenuItems(this.foundAccounts);
                        this.searching = false;
                        this.searchMessage = `Showing ${self.foundAccounts.length} results`;
                        this.changeDetection.detectChanges();
                        this.scheduleMenuHighlightUpdate();
                    },
                );
                this.searchDebounceTimeout = null;
            }, 1000);
        }
        else {
            this.foundAccounts = [];
            this.searchMenuItems = [];
            this.searching = false;
            this.searchMessage = "No results";
        }
    }

    private buildMenuItems(results: SearchResult[]): Array<{ id: string; label: string; supportingText?: string }> {
        return results.map((result, index) => {
            const id = String(result?.data?.id ?? index);
            const name = this.toDisplayText(result?.highlights?.Name, result?.data?.name || 'Unknown account');
            const code = this.toDisplayText(result?.highlights?.Code, result?.data?.code || '');
            const dba = this.toDisplayText(result?.highlights?.Dba, result?.data?.dba || '');
            const label = code ? `${name} - ${code}` : name;

            return {
                id,
                label,
                supportingText: dba || undefined
            };
        });
    }

    private toPlainText(value: unknown): string {
        if (value === null || value === undefined) {
            return '';
        }

        const normalized = Array.isArray(value) ? value.join(' ') : String(value);
        return normalized.replace(/<[^>]*>/g, '').trim();
    }

    private toDisplayText(highlightValue: unknown, fallbackValue: unknown): string {
        const fallback = this.toPlainText(fallbackValue);
        const highlight = this.toPlainText(highlightValue);
        return highlight || fallback;
    }

    private scheduleMenuHighlightUpdate(): void {
        this.cancelMenuHighlightUpdate();
        this.highlightFrameHandle = requestAnimationFrame(() => {
            this.highlightFrameHandle = null;
            this.applyMenuHighlight();
        });
    }

    private cancelMenuHighlightUpdate(): void {
        if (this.highlightFrameHandle !== null) {
            cancelAnimationFrame(this.highlightFrameHandle);
            this.highlightFrameHandle = null;
        }
    }

    private applyMenuHighlight(): void {
        const menuHost = this.searchMenuElement?.nativeElement;
        const shadowRoot = menuHost?.shadowRoot;
        const searchTerm = this.toPlainText(this.activeSearchTerm);

        if (!shadowRoot) {
            this.appliedHighlightKey = null;
            return;
        }

        // aurora-menu renders asynchronously, so re-check the rendered items rather than the bound array.
        const textParts = shadowRoot.querySelectorAll('[part="menu-item-label"], [part="menu-item-supporting-text"]');
        const highlightKey = `${searchTerm}|${Array.from(textParts).map(part => part.textContent || '').join('\u0000')}`;

        if (highlightKey === this.appliedHighlightKey) {
            return;
        }
        this.appliedHighlightKey = highlightKey;

        this.ensureHighlightStyles(shadowRoot);

        textParts.forEach(part => {
            const content = part.textContent || '';
            part.replaceChildren(...this.buildHighlightedNodes(content, searchTerm));
        });
    }

    // Styles must live inside the aurora-menu shadow root; component SCSS cannot reach it.
    private ensureHighlightStyles(shadowRoot: ShadowRoot): void {
        if (shadowRoot.getElementById(AccountSearchComponent.HighlightStyleElementId)) {
            return;
        }

        const styleElement = document.createElement('style');
        styleElement.id = AccountSearchComponent.HighlightStyleElementId;
        styleElement.textContent = `
            .account-search-highlight {
                background-color: var(--aurora-semantic-background-feedback-warning-subtle);
                color: inherit;
                font-weight: 700;
                padding: 0 1px;
            }
        `;

        shadowRoot.appendChild(styleElement);
    }

    private buildHighlightedNodes(content: string, term: string): Node[] {
        const text = this.toPlainText(content);
        if (!text) {
            return [];
        }

        if (!term) {
            return [document.createTextNode(text)];
        }

        const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(escapedTerm, 'ig');
        const nodes: Node[] = [];
        let start = 0;
        let match: RegExpExecArray | null;

        while ((match = regex.exec(text)) !== null) {
            const index = match.index;
            const matchedText = match[0];

            if (index > start) {
                nodes.push(document.createTextNode(text.slice(start, index)));
            }

            const mark = document.createElement('mark');
            mark.className = 'account-search-highlight';
            mark.textContent = matchedText;
            nodes.push(mark);

            start = index + matchedText.length;
        }

        if (start < text.length) {
            nodes.push(document.createTextNode(text.slice(start)));
        }

        return nodes;
    }

    public onSearchMenuItemClick(event: Event): void {
        const selectedId = (event as CustomEvent)?.detail?.item?.id;
        if (!selectedId) {
            return;
        }

        const match = this.foundAccounts.find(account => String(account?.data?.id) === String(selectedId));
        if (!match?.data) {
            return;
        }

        this.onSearchAccountAdded(match.data);
    }

    public onSearchAccountAdded(foundAccount: any): void {
        this.searchFocus = false;
        this.overSearch = false;
        this.changeDetection.detectChanges();
        this.router.navigate(['Work', 'Account', foundAccount.id]);
        this.clearSearchInput();
    }

    public clearSearchInput(): void {
        this.searchFocus = false;
        this.overSearch = false;
        this.searchHasValue = false;
        this.searchValue = "";
        this.foundAccounts = [];
        this.searchMenuItems = [];
        this.searching = false;
        this.activeSearchTerm = '';
        this.setDefaultSearchMessage();
        this.scheduleMenuHighlightUpdate();
    }
}
